import moment from 'moment';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  InfoIcon,
  HandsClappingIcon,
  CopyIcon,
  DotsThreeIcon,
  NotePencilIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import Latex from '../../../../../apps/frontend/src/components/global/Latex';
import getQuestionText from '../../components/solution/Question';
import { getOption, getNumericalOption } from '../../components/solution/Options';
import SolutionImage from '../../Solutions/components/community/SolutionImage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { QUESTION_TYPE } from '../../utils/Question_Type';
import {
  COMMUNITY_SOLUTION_SUPPORT_EMAIL,
  buildCommunitySolutionSupportMailto,
} from '../../utils/communitySolutionSupportMail';

const COLLAPSED_IMAGE_HEIGHT = 162;

const handleCopyId = (id) => {
  navigator.clipboard.writeText(id);
  toast.success('Solution id copied');
};

const StatusBanner = ({ solutionId, userEmail, status, rejection }) => {
  const supportMailto = buildCommunitySolutionSupportMailto(userEmail, solutionId);

  if (status === 'pending') {
    return (
      <div className="border border-dashed border-[var(--q3-yellow-normal)] bg-[var(--q3-yellow-light)] rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-[var(--q3-yellow-normal)] text-sm">
          Your solution is getting reviewed
        </div>
        <p className="text-sm text-[var(--q3-yellow-dark)] mt-1">
          We will notify you about status of your solution once it&apos;s reviewed
        </p>
      </div>
    );
  }

  if (status === 'approved') {
    return (
      <div className="border border-dashed border-[var(--q3-success-normal)] bg-[var(--q3-success-light)] rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-[var(--q3-success-normal)] text-xs md:text-sm font-bold">
          <CheckCircleIcon size={18} />
          Published
        </div>
      </div>
    );
  }

  if (status === 'rejected' || status === 'removed') {
    return (
      <div className="border border-dashed border-[var(--q3-danger-normal)] bg-[var(--q3-danger-light)] rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-[var(--q3-danger-normal)] font-semibold text-sm">
          <InfoIcon size={18} />
          Solution {status === 'removed' ? 'removed' : 'rejected'}
        </div>
        <p className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-1">
          Your solution has been {status === 'removed' ? 'removed' : 'rejected'}.
        </p>
        {rejection?.reason && (
          <p className="text-xs md:text-sm text-[var(--q3-neutral-default)]">Rejection Reason: {rejection.reason}</p>
        )}
        <p className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-1">
          If you think this is a mistake you can contact us on{' '}
          <a href={supportMailto} className="text-[var(--q3-blue-normal)]">
            {COMMUNITY_SOLUTION_SUPPORT_EMAIL}
          </a>
        </p>
        {solutionId && (
          <p className="text-xs md:text-sm text-[var(--q3-neutral-default)] mt-2">
            Use this solution id to contact us: <span className="font-semibold">{solutionId}</span>{' '}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyId(solutionId);
              }}
              className="cursor-pointer inline-flex items-center gap-1 underline text-[var(--q3-blue-normal)]"
            >
              copy id
              <CopyIcon size={12} />
            </button>
          </p>
        )}
      </div>
    );
  }

  return null;
};

const QuestionOptions = ({ question }) => {
  const currentSolution = { questionId: question, status: 'pending' };

  if (question?.type === 'numerical') {
    return getNumericalOption(currentSolution, {}, true);
  }

  return (
    <div className="w-full">
      {question?.options?.map((option, index) => getOption(currentSolution, option, index, null, true))}
    </div>
  );
};

const MySolutionListItem = ({ solution, expanded, onToggle, hasWriteAccess, maxEditCount, onEdit, userEmail }) => {
  const question = solution?.questionId;
  const isFillTheBlanks = question?.type === 'fillTheBlanks';

  const editCount = solution?.editCount ?? 0;
  const editLimitReached = typeof maxEditCount === 'number' && editCount >= maxEditCount;
  const canEdit = hasWriteAccess && !editLimitReached && solution?.status !== 'pending';

  return (
    <div className="border border-[var(--q3-stroke-light)] rounded-xl overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggle();
        }}
        className="cursor-pointer w-full flex flex-col gap-2 px-4 pt-3 pb-2 text-left"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-[var(--q3-neutral-light)]">
            {moment(solution?.createdAt).format("D MMM'YY")} | {solution?.subject?.subjectTitle} |{' '}
            {solution?.pack?.packTitle}
          </span>

          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="cursor-pointer shrink-0 flex items-center justify-center h-8 w-8 rounded-full border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)]"
                >
                  <DotsThreeIcon size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-0 border-none shadow-none bg-transparent min-w-0">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    // Defer opening the edit modal until after Radix has fully
                    // torn down this dropdown's DismissableLayer - opening it
                    // synchronously from within the item's click races the two
                    // overlays' body pointer-events bookkeeping and can leave
                    // the whole page stuck unclickable after the modal closes.
                    setTimeout(() => onEdit(), 0);
                  }}
                  className="cursor-pointer text-sm text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] shadow-md rounded-full flex items-center justify-center gap-1 px-3 py-1.5"
                >
                  <NotePencilIcon size={16} className="text-[var(--q3-danger-normal)]" /> Edit Solution
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-start justify-between gap-2">
          <div
            className={`flex-1 min-w-0 text-sm md:text-base text-[var(--q3-neutral-default)] ${expanded ? '' : 'line-clamp-1'}`}
          >
            <Latex>{question?.question?.text}</Latex>
          </div>
          {expanded ? (
            <CaretUpIcon size={16} className="shrink-0 mt-1 text-[var(--q3-neutral-light)]" />
          ) : (
            <CaretDownIcon size={16} className="shrink-0 mt-1 text-[var(--q3-neutral-light)]" />
          )}
        </div>

        {solution?.test?.testTitle && (
          <span className="text-xs text-[var(--q3-neutral-light)]">{solution.test.testTitle}</span>
        )}
      </div>

      <div className="px-4 pb-4 flex flex-col gap-3">
        {expanded && isFillTheBlanks && <div className="text-sm">{getQuestionText({ questionId: question }, 0)}</div>}
        {expanded && question && <QuestionOptions question={question} />}
        {expanded && solution?.pack?.packId && solution?.test?.testId && question?._id && (
          <Link
            to={`/${solution.pack.packId}/${solution.test.testId}/solutions?questionId=${question._id}`}
            onClick={(e) => e.stopPropagation()}
            className="cursor-pointer w-fit inline-flex items-center gap-1 text-sm font-medium text-[var(--q3-blue-normal)] underline"
          >
            View in Test
            <ArrowSquareOutIcon size={14} />
          </Link>
        )}

        <SolutionImage
          images={solution?.solution?.images}
          expanded={expanded}
          onExpandedChange={onToggle}
          collapsedHeight={COLLAPSED_IMAGE_HEIGHT}
        />

        {solution?.status === 'approved' && (
          <div className="flex items-center gap-1 whitespace-nowrap text-xs md:text-sm text-[var(--q3-neutral-default)] w-fit border border-[var(--q3-stroke-light)] rounded-full px-3 py-1.5">
            <HandsClappingIcon className="text-sm md:text-base" />
            Appreciate • {solution?.upvotes ?? 0}
          </div>
        )}

        <StatusBanner
          solutionId={solution?._id}
          userEmail={userEmail}
          status={solution?.status}
          rejection={solution?.rejection}
        />
      </div>
    </div>
  );
};

export default MySolutionListItem;
